import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import type { TFunction } from "core/entities";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { useClearSWRCache } from "core/hooks/useClearSWRCache";
import { useImpersonation } from "core/hooks/useImpersonation";
import { usePermissionChecker } from "core/hooks/usePermissionChecker";
import { usePermissionContext } from "core/hooks/usePermissionContext";
import { useRegion } from "core/hooks/useRegion";
import { useUserTokenInfo } from "core/hooks/useUserTokenInfo";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useAvailableLocales } from "../../hooks/useAvailableLocales";
import { AvailableRegionMenuItem } from "../AvailableRegionMenuItem";
import { UserAvatar } from "../UserAvatar";
import { DevMenuItems } from "./DevMenuItems";
import type { MenuOption } from "./shared";
import { useUserMenu } from "./useUserMenu";

export function UserMenu({
  menuItems = ["region-select", "logout", "user-preferences"],
}: {
  menuItems?: MenuOption[];
}) {
  const { hasSomePermissions } = usePermissionChecker();
  const canImpersonate = hasSomePermissions([
    "AbpIdentity.Users.Impersonation",
  ]);
  const { isImpersonating } = useImpersonation();
  const { anchorEl, handleCloseUserMenu, handleOpenUserMenu } = useUserMenu();
  const { isDesktop } = useBreakpoints();
  const { nickname, givenName, name } = useUserTokenInfo() || {};
  const displayName = nickname ?? name ?? givenName ?? "";
  const { t } = useTranslation("Fixhub", { keyPrefix: "Next:Core:UserMenu" });

  return (
    <>
      <Button
        variant="text"
        sx={{ color: "primary.contrastText" }}
        onClick={handleOpenUserMenu}
        color="secondary"
        data-testid="user-menu-button"
      >
        <UserAvatar />
        {isDesktop && (
          <Typography
            px={1}
            textTransform="none"
            display="flex"
            alignItems="center"
          >
            {displayName}
            <ArrowDropDownIcon />
          </Typography>
        )}
      </Button>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(anchorEl)}
        onClose={async () => {
          await handleCloseUserMenu(null);
        }}
      >
        {!isDesktop && (
          <Divider textAlign="center">
            <Typography color="text.secondary" variant="overline">
              {displayName}
            </Typography>
          </Divider>
        )}
        <DevMenuItems t={t} handleCloseUserMenu={handleCloseUserMenu} />
        {canImpersonate && !isImpersonating && (
          <MenuItem
            key="impersonate"
            onClick={async () => {
              await handleCloseUserMenu("impersonate");
            }}
          >
            {t("option.impersonate", "Impersonate User")}
          </MenuItem>
        )}
        {menuItems.map((option) => (
          <MenuItem
            key={option}
            onClick={async () => {
              await handleCloseUserMenu(option);
            }}
          >
            {t(`option.${option}`)}
          </MenuItem>
        ))}
        <LanguageMenuItems t={t} handleCloseUserMenu={handleCloseUserMenu} />
        <RegionMenuItems t={t} handleCloseUserMenu={handleCloseUserMenu} />
      </Menu>
    </>
  );
}

function LanguageMenuItems({
  t,
  handleCloseUserMenu,
}: {
  t: TFunction;
  handleCloseUserMenu: ReturnType<typeof useUserMenu>["handleCloseUserMenu"];
}) {
  const { locale } = useRouter();
  const { availableLocales } = useAvailableLocales();
  const { t: tCore } = useTranslation("Fixhub");
  const { push, pathname, query, asPath } = useRouter();

  return (
    <>
      <Divider textAlign="center">
        <Typography color="text.secondary" variant="overline">
          {t("language")}
        </Typography>
      </Divider>
      {availableLocales?.map((selectedLocale) => (
        <MenuItem
          selected={locale === selectedLocale}
          key={selectedLocale}
          onClick={() => {
            handleCloseUserMenu(null);
            push({ pathname, query }, asPath, { locale: selectedLocale });
          }}
        >
          {tCore(`Next:Core:LanguageCodes.${selectedLocale}`)}
        </MenuItem>
      ))}
    </>
  );
}

function RegionMenuItems({
  t,
  handleCloseUserMenu,
}: {
  t: TFunction;
  handleCloseUserMenu: ReturnType<typeof useUserMenu>["handleCloseUserMenu"];
}) {
  const currentRegion = useRegion();
  const { pathname, query, replace } = useRouter();
  const clearCache = useClearSWRCache();
  const { t: tCore } = useTranslation("Fixhub");
  const { data: permissionContextData } = usePermissionContext();
  const brandRegions = permissionContextData?.userAvailableRegions ?? [];
  if (brandRegions.length === 0) {
    return null;
  }
  return (
    <>
      <Divider textAlign="center">
        <Typography color="text.secondary" variant="overline">
          {t("region")}
        </Typography>
      </Divider>
      {brandRegions?.map((menuItemRegion) => (
        <AvailableRegionMenuItem
          selected={currentRegion === menuItemRegion}
          onClick={async () => {
            if (currentRegion !== menuItemRegion) {
              await replace({
                pathname,
                query: { ...query, region: menuItemRegion },
              });
              clearCache();
            }
            handleCloseUserMenu(null);
          }}
          label={tCore(`Region:RegionName.${menuItemRegion}`)}
          key={menuItemRegion}
        />
      ))}
    </>
  );
}
